import React, { useState } from "react";

import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Download,
  EyeOff,
  FileQuestion,
  FileText,
  GitCompare,
  History,
  Loader2,
} from "lucide-react";

import BriefingRunDialog from "./BriefingRunDialog";
import ConfirmNote from "./ConfirmNote";
import ConfirmPlanPanel from "./ConfirmPlanPanel";
import { FLOW, planLabel } from "./flowCopy";
import { ingestedAt, isViewable, pageUnit } from "./PlanCard";
import { planFlow } from "./planFlow";
import { planGapOf, planNextStep } from "./planNext";
import PlanStateLabel, { viewStateOf } from "./PlanStateLabel";
import {
  ago,
  ComingSoon,
  NextStepCard,
  PlanNav,
  shortDate,
  STEP_EYEBROW,
  stepHeading,
} from "./shared";
import SlideViewer, { resolveAssetUrl } from "./SlideViewer";
import { useAccountPlanFile } from "../../Hooks/useAccountPlan";
import type { AccountPlanOverview } from "../../Types/AccountPlan";

/**
 * The viewer's eyebrow names the step it is on, in the step bar's words (F-UF-F21):
 * the sponsor's step 3 (View source); the team's step 2 while the version waits on
 * Confirm plan; and, for a version already confirmed, View source with its number:
 * that version was opened to read it, not to confirm it.
 */
export const viewerHeading = (
  isES: boolean,
  active: { confirmed?: boolean; plan_version?: number | null } | null
): string => {
  if (isES) return stepHeading("ES", "viewer") as string;
  if (!active || active.confirmed === false)
    return `${stepHeading("AE", "viewer")} — what ExecBridge read`;
  const n = planLabel(active, "");
  return `${FLOW.viewSource}${n ? ` · ${n}` : ""} — what ExecBridge read`;
};

interface Props {
  accountId: string;
  data: AccountPlanOverview;
  nav: PlanNav;
  fileId: string | null;
  slide: number | null;
  onSelectFile: (fileId: string) => void;
  onSelectSlide: (page: number) => void;
  onGoToPlan: () => void;
  /** Refresh the overview (after Confirm plan / Not this file). */
  onChanged?: () => void;
}

const PlanViewerTab: React.FC<Props> = ({
  accountId,
  data,
  nav,
  fileId,
  slide,
  onSelectFile,
  onSelectSlide,
  onGoToPlan,
  onChanged,
}) => {
  const isES = nav.persona === "ES";
  // The version whose briefing was just started from Confirm plan: its run shows
  // in a pop-up here (Sushant, 2026-11-03), fed by the overview's polling.
  const [watching, setWatching] = useState<string | null>(null);
  const briefingDialog = (
    <BriefingRunDialog
      open={!!watching}
      file={data.files.find(f => f.id === watching) || null}
      onClose={() => setWatching(null)}
      onReview={() => {
        setWatching(null);
        nav.go("sponsor", { file: null, slide: null });
      }}
    />
  );
  const viewable = data.files.filter(isViewable);
  // Confirm plan (Phase 2): the team's step 2 opens on the upload waiting to be
  // confirmed, with the confirm panel above its slides. The sponsor never sees it.
  const flow = planFlow(data);
  const toConfirm = isES ? null : flow.toConfirm;
  const activeId = fileId || toConfirm?.id || data.current_file_id;
  const active = data.files.find(f => f.id === activeId) || null;
  // Only a version this plan lists is fetched: a malformed or stale ?file reads
  // "Unknown version" instead of asking the server and failing (F-46).
  const { file, loading, error, reload } = useAccountPlanFile(
    accountId,
    active ? activeId : null
  );
  const pages = file?.parsed?.pages || [];

  const header = (
    <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <div className={`mb-2 ${STEP_EYEBROW}`}>
          {viewerHeading(isES, active)}
        </div>
        {/* STP-13, F-UF-E16: the old source-plan name never appears here. */}
        <h2 className="text-2xl font-bold text-gray-900">
          {isES ? "The account plan" : "What ExecBridge read"}
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          The original deck. Every citation in the briefing links back to a
          specific slide here.
        </p>
        {isES && active && (
          // The sponsor reads the newest version: draft until published (Q1 = C + A).
          <PlanStateLabel
            className="mt-2"
            variant="banner"
            state={viewStateOf(active.published)}
            publishedAt={active.published?.at}
          />
        )}
      </div>
      {file?.download_url && (
        <a
          href={resolveAssetUrl(file.download_url) || undefined}
          className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-[13px] font-semibold text-slate-600 transition hover:border-purple-300 hover:bg-purple-50 hover:text-[#8538d4] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
        >
          <Download className="h-4 w-4" aria-hidden /> Download original
        </a>
      )}
    </div>
  );

  if (!activeId) {
    // Name the real state (UI tour A item 4): being read, stalled, stopped or
    // nothing uploaded, with that state's next step or none.
    const gap = isES ? null : planGapOf(data, flow, nav.canAct("AE"));
    const step = isES
      ? null
      : planNextStep(data, flow, {
          ae: nav.canAct("AE"),
          csm: nav.canAct("CSM"),
        });
    return (
      <div>
        {header}
        {briefingDialog}
        <div className="rounded-xl border border-dashed border-gray-300 bg-white p-10 text-center">
          <FileQuestion
            className="mx-auto mb-3 h-12 w-12 text-gray-300"
            aria-hidden
          />
          <div className="mb-1 text-lg font-semibold text-gray-700">
            Nothing to view yet
          </div>
          <p className="text-sm text-gray-500" data-testid="viewer-empty">
            {isES
              ? "Your account team hasn't shared an account plan yet."
              : gap?.kind === "none"
                ? nav.canAct("AE")
                  ? "No plan uploaded yet. Upload one in the Upload step."
                  : `No plan uploaded yet. ${FLOW.waitingAeUpload}.`
                : gap?.text}
          </p>
        </div>
        {isES ? (
          <NextStepCard
            label="Next step"
            action={{
              label: "View Briefing Questions",
              onClick: () => nav.go("qa"),
            }}
          >
            Nothing to view yet. See which Briefing Questions ExecBridge will
            answer once the plan arrives.
          </NextStepCard>
        ) : step && step.tab !== "viewer" ? (
          <NextStepCard
            label="Next step"
            action={{
              label: step.label,
              onClick: () =>
                step.tab === "plan" && !step.extra
                  ? onGoToPlan()
                  : step.extra
                    ? nav.go(step.tab, step.extra)
                    : nav.go(step.tab),
            }}
          >
            {gap?.kind === "none"
              ? "Upload the source deck so the briefing has something to ground in."
              : gap?.text}
          </NextStepCard>
        ) : null}
      </div>
    );
  }

  const activeAt = active ? ingestedAt(active) : null;
  // The bar says when it was uploaded, not "ingested" (F-UF-E16).
  const uploadedAt = active ? active.created_at || activeAt : null;
  const confirming = !!toConfirm && active?.id === toConfirm.id;

  return (
    <div>
      {header}
      {briefingDialog}
      {confirming && toConfirm && (
        <ConfirmPlanPanel
          accountId={accountId}
          data={data}
          file={toConfirm}
          nav={nav}
          onChanged={() => onChanged?.()}
          onBriefingStarted={setWatching}
        />
      )}
      {!isES &&
        !confirming &&
        active?.confirmed &&
        active.confirmed_by_name && (
          <p
            data-testid="confirmed-by"
            className="-mt-3 mb-4 flex items-center gap-1.5 text-xs text-gray-500"
          >
            <CheckCircle2
              className="h-3.5 w-3.5 text-emerald-600"
              aria-hidden
            />
            Confirmed by {active.confirmed_by_name}
            {active.confirmed_at ? ` · ${shortDate(active.confirmed_at)}` : ""}
          </p>
        )}
      {!isES && !confirming && active?.confirmed && (
        <ConfirmNote file={active} className="-mt-2 mb-4" />
      )}
      <div className="space-y-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start">
          <details className="group min-w-0 flex-1 rounded-xl border border-gray-200 bg-white shadow-sm">
            <summary className="flex cursor-pointer list-none flex-wrap items-center justify-between gap-x-4 gap-y-1 rounded-xl px-4 py-3 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50 [&::-webkit-details-marker]:hidden">
              <span className="flex min-w-0 items-center gap-2 text-sm">
                <ChevronRight
                  className="h-4 w-4 shrink-0 text-gray-400 transition-transform group-open:rotate-90"
                  aria-hidden
                />
                <span className="font-mono text-[10px] uppercase tracking-widest text-gray-400">
                  Viewing
                </span>
                {active ? (
                  <>
                    <span className="font-mono font-semibold text-gray-800">
                      {planLabel(active, "New upload")}
                    </span>
                    <span className="truncate text-gray-600">
                      {active.filename} · uploaded {shortDate(uploadedAt)} (
                      {ago(uploadedAt)})
                    </span>
                    {active.id === data.current_file_id && (
                      <span className="shrink-0 rounded bg-purple-100 px-1.5 py-0.5 text-[10px] font-semibold text-purple-800">
                        CURRENT
                      </span>
                    )}
                  </>
                ) : (
                  <span className="text-gray-600">Unknown version</span>
                )}
              </span>
              <span className="flex shrink-0 items-center gap-3 text-xs text-gray-500">
                <span>
                  Plan refresh:{" "}
                  <span className="font-medium text-gray-700">
                    every{" "}
                    {data.freshness?.refresh_days ??
                      data.settings.refresh_days ??
                      30}{" "}
                    days
                  </span>
                </span>
                <span className="text-gray-300" aria-hidden>
                  ·
                </span>
                <span className="inline-flex items-center gap-1 font-semibold text-[#8538d4]">
                  <History className="h-3.5 w-3.5" aria-hidden /> Version
                  history ({viewable.length})
                </span>
              </span>
            </summary>
            <ul className="divide-y divide-gray-100 border-t border-gray-100">
              {viewable.map(f => {
                const selected = f.id === activeId;
                const at = ingestedAt(f);
                return (
                  <li key={f.id}>
                    <button
                      type="button"
                      onClick={() => onSelectFile(f.id)}
                      aria-current={selected ? "true" : undefined}
                      className={`flex w-full items-center gap-3 px-4 py-2 text-left text-sm hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#974ae4]/50 ${
                        selected ? "bg-purple-50/40" : ""
                      }`}
                    >
                      <span className="w-10 shrink-0 font-mono font-semibold text-gray-800">
                        {planLabel(f, "New")}
                      </span>
                      <FileText
                        className={`h-3.5 w-3.5 shrink-0 ${selected ? "text-[#974ae4]" : "text-gray-400"}`}
                        aria-hidden
                      />
                      <span className="min-w-0 flex-1 truncate text-gray-700">
                        {f.filename}
                      </span>
                      {f.id === data.current_file_id && (
                        <span className="shrink-0 rounded bg-purple-100 px-1.5 py-0.5 text-[10px] font-semibold text-purple-800">
                          CURRENT
                        </span>
                      )}
                      <span className="hidden shrink-0 text-[11px] text-gray-500 sm:inline">
                        {f.page_count
                          ? `${f.page_count} ${pageUnit(f, f.page_count)} · `
                          : ""}
                        {shortDate(at)} · {ago(at)}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
            <div className="flex justify-end border-t border-gray-100 px-4 py-2">
              <ComingSoon
                label="Compare versions"
                icon={GitCompare}
                title="Side-by-side version compare comes later"
              />
            </div>
          </details>
        </div>

        {/* The partial-read note is for the plan's owners; the sponsor (and Preview
            as sponsor / the reading view, which render as ES) never sees failure
            wording (spec INV-06, F-40). */}
        {!isES && file?.status === "partial" && file.error && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            {file.error}
          </div>
        )}

        {file?.content_hidden ? (
          // Possibly another customer's deck (UI tour, 2026-11-03): the server sends
          // its content to the AE only, until they decide.
          <div
            role="status"
            data-testid="viewer-content-hidden"
            className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          >
            <EyeOff className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>
              <span className="font-semibold">{FLOW.slidesHiddenTitle}</span>{" "}
              {FLOW.slidesHiddenBody}
            </span>
          </div>
        ) : loading && !file ? (
          <div
            className="flex items-center justify-center gap-2 py-16 text-sm text-gray-500"
            role="status"
          >
            <Loader2
              className="h-5 w-5 animate-spin text-[#974ae4]"
              aria-hidden
            />{" "}
            Loading slides…
          </div>
        ) : file?.parsed?.pages ? (
          <SlideViewer
            key={file.id}
            pages={file.parsed.pages}
            selectedPage={slide}
            onSelectPage={onSelectSlide}
            onImagesExpired={() => void reload()}
          />
        ) : error ? (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        ) : null}
      </div>

      {pages.length > 0 &&
        !confirming &&
        (isES ? (
          <NextStepCard
            label="Next step"
            action={{
              label: "Back to briefing",
              onClick: () => nav.go("sponsor"),
            }}
          >
            Every claim in the Account Briefing links back to a slide here. Head
            back to the briefing when you&apos;re done checking.
          </NextStepCard>
        ) : (
          <NextStepCard
            label="Next step"
            action={{
              label: "Review briefing",
              onClick: () => nav.go("sponsor"),
            }}
          >
            {data.current_review
              ? "Review the draft the sponsor sees — every claim links back to a slide here."
              : "The draft briefing is written from this plan; follow it in Review briefing."}
          </NextStepCard>
        ))}
    </div>
  );
};

export default PlanViewerTab;
